<template>
  <va-card class="card-container">
    <va-card-title>
      <slot name="title">
        {{ $t("activations.title") }}
      </slot>
    </va-card-title>
    <va-card-content>
      <va-data-table
        :no-data-html="$t('activations.noItems')"
        :items="state.activations"
        :columns="columns"
        :loading="state.loading"
      >
        <template #header(id)>{{ $t("activations.id") }}</template>
        <template #header(entitlement)>
          {{ $t("entitlements.title") }}
        </template>
        <template #header(createdAt)>
          {{ $t("activations.createdAt") }}
        </template>
        <template #header(lastSynchronizarion)>
          {{ $t("activations.lastSync") }}
        </template>
        <template #header(hostName)>
          {{ $t("activations.hostName") }}
        </template>
        <template #header(aliasName)>
          {{ $t("activations.aliasName") }}
        </template>
        <template #header(os)>
          {{ $t("activations.os") }}
        </template>
        <template #header(osVersion)>
          {{ $t("activations.osVersion") }}
        </template>
        <template #header(restrictedMode)>
          {{ $t("activations.restrictedMode") }}
        </template>
        <template #header(deactivate)>
          {{ $t("activations.deactivation") }}
        </template>

        <template #cell(id)="{ source: id }">
          <span class="mono">{{ id }}</span>
          <button-clipboard-copy :field="$t('activations.id')" :value="id" />
        </template>
        <template #cell(entitlement)="{ source: entitlement }">
          <span v-if="entitlement?.id">
            <router-link :to="{ path: entitlementLink(entitlement.id) }">
              {{ entitlement.name || entitlement.id }}
            </router-link>
            <button-clipboard-copy
              :field="$t('entitlements.id')"
              :value="entitlement.id"
            />
          </span>
          <span v-else class="text-muted">--</span>
        </template>
        <template #cell(createdAt)="{ source }">
          {{ source ? printDate(source) : "--" }}
        </template>
        <template #cell(lastSynchronizarion)="{ source }">
          {{ source ? printDate(source) : "--" }}
        </template>
        <template #cell(restrictedMode)="{ source }">
          <va-badge
            :text="source ? $t('general.yes') : $t('general.no')"
            :color="source ? 'success' : 'error'"
          />
        </template>
        <template #cell(deactivate)="{ rowIndex }">
          <va-button
            color="#DC2626"
            flat
            icon="delete"
            :disabled="!!state.deactivatingId"
            @click="openDeactivation(rowIndex)"
          />
        </template>
      </va-data-table>
    </va-card-content>
    <va-card-content>
      <va-pagination
        class="pagination"
        input
        color="#000000"
        v-model="state.currentPage"
        :hide-on-single-page="true"
        :pages="state.totalPages"
        :page-size="state.perPage"
      />
    </va-card-content>
  </va-card>
</template>

<style scoped>
.mono {
  font-family: monospace;
}

.pagination {
  justify-content: center;
}
</style>

<script setup>
import { computed, inject, reactive, watch } from "vue";
import { useI18n } from "vue-i18n";
import { callDeleteActivation, callFetchActivations } from "@/api/licensing";
import ButtonClipboardCopy from "@/components/ButtonClipboardCopy.vue";
import { printDate } from "@/utils";
import { entitlementLink } from "@/utils/licensing";

const props = defineProps({
  userId: {
    type: String,
    required: true
  }
});

const { t } = useI18n();
const toasts = inject("toasts");
const handleError = inject("handleError");

// Filter strategies for finding user activations
// We'll try these in order, but cache successful strategy to avoid multiple requests
const FILTER_STRATEGIES = [
  userId => ({ userId }),
  userId => ({ assignmentAssigneeId: userId }),
  // Removed potentially invalid filters that could return all activations
  // userId => ({ assigneeId: userId }), // This filter may not exist in API
  userId => ({ assignmentAssigneeId: userId, assignmentAssigneeType: "User" })
];

const columns = computed(() => [
  { key: "id" },
  { key: "entitlement" },
  { key: "createdAt" },
  { key: "lastSynchronizarion" },
  { key: "hostName" },
  { key: "aliasName" },
  { key: "os" },
  { key: "osVersion" },
  { key: "restrictedMode" },
  { key: "deactivate" }
]);

const state = reactive({
  activations: [],
  loading: false,
  currentPage: 1,
  totalItems: 0,
  perPage: 25,
  totalPages: 0,
  lastLoadedPage: undefined,
  deactivatingId: ""
});

let activeFilterIndex = null;

const buildParams = (page, extra = {}) => ({
  count: state.perPage,
  page,
  ...extra
});

const getTotalPages = total =>
  Math.floor(total / state.perPage) +
  (total % state.perPage === 0 ? 0 : 1);

const mapActivation = item => ({
  id: item?.id,
  entitlementId:
    item?.entitlementId ??
    item?.entitlement?.id ??
    item?.assignment?.entitlementId,
  entitlementName:
    item?.entitlement?.displayName ??
    item?.entitlement?.name ??
    item?.assignment?.entitlement?.displayName,
  entitlement: {
    id:
      item?.entitlementId ??
      item?.entitlement?.id ??
      item?.assignment?.entitlementId,
    name:
      item?.entitlement?.displayName ??
      item?.entitlement?.name ??
      item?.assignment?.entitlement?.displayName
  },
  createdAt: item?.createdAt,
  lastSynchronizarion: item?.lastRefreshedAt,
  os: item?.os,
  osVersion: item?.osVersion,
  hostName: item?.hostName,
  aliasName: item?.aliasName,
  restrictedMode: item?.metadata?.RestrictedMode === "true"
});

const loadActivations = async (page = 1, forceReload = false) => {
  if (state.loading || !props.userId) {
    return;
  }

  if (!forceReload && state.lastLoadedPage === page) {
    return;
  }

  state.loading = true;

  // Reset strategy on force reload or if invalid
  if (
    forceReload ||
    activeFilterIndex === null ||
    activeFilterIndex < 0 ||
    activeFilterIndex >= FILTER_STRATEGIES.length
  ) {
    activeFilterIndex = null;
  }

  // If we have a cached successful strategy, use it first
  // This avoids multiple API calls on subsequent page loads
  const strategyIndices = FILTER_STRATEGIES.map((_, index) => index);
  const orderedStrategies =
    activeFilterIndex !== null
      ? [activeFilterIndex, ...strategyIndices.filter(i => i !== activeFilterIndex)]
      : strategyIndices;

  let lastError;
  let found = false;

  for (const strategyIndex of orderedStrategies) {
    const params = buildParams(
      page,
      FILTER_STRATEGIES[strategyIndex](props.userId)
    );

    try {
      const resp = await callFetchActivations({ params });
      const items = resp?.data?.items ?? [];
      const total = resp?.data?.total ?? items.length;

      // Accept the first successful response, even if empty
      // This prevents trying other filters that might return unfiltered results
      state.activations = items.map(mapActivation);
      state.totalItems = total;
      state.totalPages = getTotalPages(total);
      state.currentPage = page;
      state.lastLoadedPage = page;
      activeFilterIndex = strategyIndex;
      found = true;
      break;
    } catch (error) {
      lastError = error;
      // Only try next strategy if this one actually failed (404, 400, etc)
      // Don't continue if we get a successful response with no data
      if (error?.response?.status < 400 || error?.response?.status >= 500) {
        // Server error or other issue - stop trying
        break;
      }
    }
  }

  if (!found) {
    state.activations = [];
    state.totalItems = 0;
    state.totalPages = 0;
    state.currentPage = page;
    state.lastLoadedPage = page;
    if (lastError) {
      handleError?.(lastError?.response?.data || lastError);
    }
  }

  state.loading = false;
};

const openDeactivation = rowIndex => {
  const activation = state.activations[rowIndex];
  if (!activation?.id) {
    return;
  }
  state.deactivatingId = activation.id;
  deactivateActivation();
};

const deactivateActivation = async () => {
  if (!state.deactivatingId) {
    return;
  }
  try {
    await callDeleteActivation(state.deactivatingId);
    const itemIndex = state.activations.findIndex(
      activation => activation.id === state.deactivatingId
    );
    if (itemIndex !== -1) {
      state.activations.splice(itemIndex, 1);
      state.totalItems = Math.max(state.totalItems - 1, 0);
      state.totalPages = getTotalPages(state.totalItems);
      if (state.activations.length === 0 && state.currentPage > 1) {
        await loadActivations(state.currentPage - 1, true);
      }
    }
    toasts?.info(t("activations.deactivateSuccess"));
  } catch (error) {
    handleError?.(error?.response?.data || error);
  } finally {
    state.deactivatingId = "";
  }
};

watch(
  () => state.currentPage,
  async currentPage => {
    await loadActivations(currentPage);
  }
);

watch(
  () => props.userId,
  async newUserId => {
    if (newUserId) {
      state.currentPage = 1;
      state.lastLoadedPage = undefined;
      activeFilterIndex = null;
      await loadActivations(1, true);
    } else {
      state.activations = [];
      state.totalItems = 0;
      state.totalPages = 0;
    }
  },
  { immediate: true }
);
</script>
